import '@/styles/loginStyles.scss';
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CapTracLogo from '@/components/CapTracLogo';
import * as Sentry from '@sentry/react';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isValidatingToken, setIsValidatingToken] = useState(true);

    // Remove sentry-feedback div if they exist when the component mounts
    useEffect(() => {
        const sentryFeedbackDiv = document.getElementById('sentry-feedback');
        if (sentryFeedbackDiv && sentryFeedbackDiv.parentNode) {
            sentryFeedbackDiv.parentNode.removeChild(sentryFeedbackDiv);
        }
    }, []);

    // Validate the reset token when the component mounts
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                navigate('/unauthorized');
                return;
            }

            try {
                const response = await fetch(`/api/User/validate-create-password-token?token=${encodeURIComponent(token)}`);
                const data = await response.json();

                if (!data.valid) {
                    navigate('/unauthorized');
                }
            } catch (error) {
                Sentry.captureException(error);
                console.error('Token validation failed:', error);
                navigate('/unauthorized');
            } finally {
                setIsValidatingToken(false);
            }
        };

        validateToken();
    }, [token, navigate]);

    const ResetPassword = async (e) => {
        e.preventDefault();

        if (!newPassword || !confirmPassword) {
            toast.error('Both fields are required');
            return;
        }

        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        const passwordRegex = /^(?=.*[A-Z])(?=.*\d).{8,}$/;
        if (!passwordRegex.test(newPassword)) {
            toast.error('Password must be at least 8 characters long, contain at least one uppercase letter, and one number');
            return;
        }

        try {
            setIsLoading(true);
            const response = await fetch('/api/User/reset-password', {
                method: 'Post',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ token, newPassword, confirmPassword })
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Password has been reset.');
                setTimeout(() => {
                    navigate('/');
                }, 3000);
            } else {
                // Handle API error responses (e.g., invalid token, user not found)
                toast.error(data.message || 'An error occurred. Please try again later.');
            }

        } catch (e) {
            Sentry.captureException(e);
            console.log(e);
            toast.error('An error occurred. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    }

    if (isValidatingToken) {
        return (
            <div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
                    <CapTracLogo scale={0.6} capColor="var(--primary-color)" hexagonColor="var(--primary-color)" />
                </div>
                <div style={{ textAlign: 'center', color: 'var(--text-color)' }}>
                    Validating reset token...
                </div>
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
                <CapTracLogo scale={0.6} capColor="var(--primary-color)" hexagonColor="var(--primary-color)" />
            </div>
            <div id="reset-nav">
                <input
                    type="password"
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                />
                <input
                    type="password"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                />
            </div>
            <div id="reset-actions">
                <Link to="/">Back to Sign in</Link>
                <button
                    type="submit"
                    disabled={isLoading }
                    onClick={ResetPassword}
                >
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                </button>
            </div>
</div>
    )
}

export default ResetPassword;